/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
  *      2       | 30/05/2026 | Lic. José Luis Aragón Cervantes  | Limpieza de caracteres no imprimibles del lector
 *      3       | 11/06/2026 | Lic. José Luis Aragón Cervantes  | Oculta botón de importación de lecturas fuera de línea
 *      4       | 30/08/2026 | Lic. José Luis Aragón Cervantes  | Elimina tecla F12 de borrado de venta; la elimina el componente padre con Alt+F12
 *      5       | 23/09/2026 | LIC. José Luis Aragón C.                       | Deshabilita autocomplete del lector
 * --------------------------------------------------------------------------------------- */

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentPaste from '@mui/icons-material/ContentPaste';
import { leerTextoPortapapeles } from '../../helpers/portapapeles';
import { useVentas } from '../../context/Ventas/VentasContext';
import { useSesion } from '../../context/System/SesionContext';

const SizeSoftXLectorCodigoBarras = forwardRef(function SizeSoftXLectorCodigoBarras(props, ref) {
  const { handleSetCliente, TipoVenta = "VENTA", inputRefinter, disabled } = props;

  const { parametrosCajaForm } = useSesion();
 
  const inputRef = useRef(null);

  const {
    handleRegistrarArticuloSubmitVenta,
    handleRegistrarArticuloSubmitPreVenta,
    handleCargarEscaneosFromFile,
    handleViewFormDescuentos,
    handleArirModalBuscarArticulos,
    statusKeyActions = false,
    handleArirModalFirmarVenta,
    handleArirModalCobrarVenta,
    handleClickEliminarPartidaVentas,
    partidaSeleccionada,
    handleBuscarClienteFromClave,
    barcodeAnterior,
    handleAbrirValidarCliente,
  } = useVentas();

  const [barcode, setBarcode] = useState("");
  const barcodeRef = useRef("");

  const setBarcodeValue = (value) => {
    barcodeRef.current = value;
    setBarcode(value);
  };

  const normalizeBarcode = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\u0000-\u001F\u007F]/g, "")
      .trim()
      .toUpperCase();

  const handleChangeBarcode = (event) => {
    setBarcodeValue(normalizeBarcode(event.target.value));
  };

  const handlePegarPortapapeles = async () => {
    if (statusKeyActions || disabled) return;
    try {
      const texto = await leerTextoPortapapeles();
      if (texto) setBarcodeValue(normalizeBarcode(texto));
    } catch {}
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.click?.();
    el.select?.();
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      inputRef.current?.click?.();
      inputRef.current?.select?.();
    },
    clear: () => setBarcodeValue(''),
    getValue: () => barcode,
  }), [barcode]);

  useEffect(() => {
    const focusBarcode = () => {
      if (statusKeyActions) return;
      const el = inputRef.current;
      if (!el) return;

      // Si ya está, no estorbes
      if (document.activeElement === el) return;

      el.focus();
      el.click?.();
      el.select?.();
    };

    // Enfoca al entrar en modo activo
    focusBarcode();

    const shouldIgnoreTarget = (target) => {
      if (!target) return false;

      // Si el click fue en otro campo donde sí quieren escribir, NO robes el foco
      const editable =
        target.closest?.('input, textarea, [contenteditable="true"]');

      // Si quieres que NI SIQUIERA eso se respete, quita esta condición
      return Boolean(editable) && target.id !== "txtCodigoBarras";
    };

    const onAnyPointer = (e) => {
      if (statusKeyActions) return;
      if (shouldIgnoreTarget(e.target)) return;

      // OJO: usar requestAnimationFrame ayuda cuando el DOM anda moviéndose
      requestAnimationFrame(() => focusBarcode());
    };

    document.addEventListener("pointerdown", onAnyPointer, true);
    document.addEventListener("mousedown", onAnyPointer, true);
    document.addEventListener("touchstart", onAnyPointer, true);

    // También por si cambian de ventana/tab
    window.addEventListener("focus", focusBarcode);

    return () => {
      document.removeEventListener("pointerdown", onAnyPointer, true);
      document.removeEventListener("mousedown", onAnyPointer, true);
      document.removeEventListener("touchstart", onAnyPointer, true);
      window.removeEventListener("focus", focusBarcode);
    };
  }, [statusKeyActions]);

  const handleKeyDown = async (event) => {
    if (statusKeyActions) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      const codigoBarras = normalizeBarcode(barcodeRef.current);

      if(codigoBarras.includes("+"))
        {
          const repeticiones = parseInt(codigoBarras.replace('+', ''), 10);
          if (!barcodeAnterior || barcodeAnterior.length < 5) return;
          if (!isNaN(repeticiones) && repeticiones > 0) {
            for (let i = 0; i < repeticiones; i++) {
              if (TipoVenta === "VENTA") {
                await handleRegistrarArticuloSubmitVenta(barcodeAnterior,parametrosCajaForm.dominio);
              } else {
                await handleRegistrarArticuloSubmitPreVenta(barcodeAnterior,parametrosCajaForm.dominio);
              }
            }
          }
          setBarcodeValue("");
          return;
      }

      if (codigoBarras.includes(".'LAUNION'.")) {
        handleAbrirValidarCliente();
        setBarcodeValue('');
        return;
      }

      if (!codigoBarras) {
        setBarcodeValue('');
        return;
      }

      if (TipoVenta === "VENTA")
      {
        await handleRegistrarArticuloSubmitVenta(codigoBarras, parametrosCajaForm.dominio);
      }
      else
      {
        await handleRegistrarArticuloSubmitPreVenta(codigoBarras, parametrosCajaForm.dominio);
      }

      setBarcodeValue('');
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase()=== 'F1') {
      event.preventDefault();
      event.stopPropagation();
      handleArirModalBuscarArticulos();
      setBarcodeValue('');
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase() === 'F10') {
      event.preventDefault();
      event.stopPropagation();
      if (parametrosCajaForm.tipoventa === "VENTA") handleArirModalCobrarVenta();
      else if (parametrosCajaForm.tipoventa === "PREVENTA") handleArirModalFirmarVenta();
      setBarcodeValue('');
      return;
    }

    if (event.ctrlKey && event.key.trim().toUpperCase() === 'D') {
      event.preventDefault();
      event.stopPropagation();
      handleBuscarClienteFromClave("0");
      return;
    }

    if (event.altKey && event.key.trim().toUpperCase() === 'F7') {
      event.preventDefault();
      event.stopPropagation();
      handleClickEliminarPartidaVentas(partidaSeleccionada.idpartida);
      setBarcodeValue('');
      return;
    }

    if (event.key === 'Delete' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setBarcodeValue('');
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      if(!barcodeRef.current) return;
      const codex = barcodeRef.current.slice(0, -1);
      setBarcodeValue(codex);
      return;
    }

    const invalidKeys = [
      'ArrowUp','ArrowDown','ArrowRight','ArrowLeft','Insert','Home','End',
      'PageDown','PageUp','ContextMenu','AltGraph','Meta','Tab',
      'Alt','Control','Shift','CapsLock','NumLock','ScrollLock','Pause',
      'F1','F2','F3','F4','F5','F6','F7','F8','F9','F10','F11'
    ];

    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    if ((event.key || '').length !== 1) {
      return;
    }

    // El carácter lo inserta onChange (teclado físico, móvil, IME o pegado).
    return;
  };

  return (
    <div className="framedetallesventa">
      <h2 className="framedetallesventaLabel">Lector códigos</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          id="txtCodigoBarras"
          className="txtCodigoBarras"
          ref={inputRef}
          type="text"
          autoComplete="off"
          name="lector-codigo-barras"
          aria-autocomplete="none"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          value={barcode}
          onChange={handleChangeBarcode}
          onKeyDown={handleKeyDown}
          placeholder="Escanear los códigos de barras aquí"
          disabled={statusKeyActions || disabled}
          style={{ flex: 1, minWidth: 0 }}
        />
        <Tooltip title="Pegar">
          <span>
            <IconButton size="small" aria-label="Pegar" title="Pegar" onClick={handlePegarPortapapeles} disabled={statusKeyActions || disabled}>
              <ContentPaste fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {/* <button
        onClick={() => document.getElementById('inputArchivo').click()}
        className="custom-file-upload"
        type="button"
        disabled={disabled}
      >
        Cargar escaneos desde archivo.txt
      </button>

      <input
        type="file"
        id="inputArchivo"
        accept=".txt"
        style={{ display: 'none' }}
        onChange={handleCargarEscaneosFromFile}
      /> */}
    </div>
  );
});

export default SizeSoftXLectorCodigoBarras;


